import { CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  CardProps,
  Card,
  SkeletonLoader,
  toast,
} from '@insight-health-ai/roseate/components';
import { Lightbulb02, Copy02, Printer } from '@insight-health-ai/roseate/icons';
import { Empty, Segmented } from 'antd';
import moment from 'moment';
import {
  parseSummary,
  sanitizeSummaryAsText,
  copyToClipboard,
  copyRichTextToClipboard,
  sanitizeSummaryAsHtml,
  formatStaticDate,
} from 'src/utils';
import { useReactToPrint } from 'react-to-print';
import {
  useGetSummaryList,
  UseGetSummaryListParams,
  useRateSummary,
  useUpdateSummary,
} from 'src/hooks/summary';
import { showToastSuccess } from 'src/utilities/toast';
import { Encounter, EncounterSummary } from 'src/models';
import { Rating } from './rating';
import { Editor } from './editor';
import { debounce } from 'lodash';
import { useScreenSize } from '@insight-health-ai/roseate/utils';

type SummaryCardProps = {
  filters: Pick<
    UseGetSummaryListParams,
    'encounterId' | 'encounterPhase' | 'type' | 'serviceRequestId'
  >;
  getSummary?: (filters: SummaryCardProps['filters']) => EncounterSummary;
  encounter?: Partial<Pick<Encounter, 'description' | 'created_at'>>;
  title?: string;
  emptyPlaceholder?: string;
  editable?: boolean;
  editorProps?: Pick<
    React.ComponentProps<typeof Editor>,
    'showToolbar' | 'className' | 'minHeight'
  >;
  getCustomActions?: (
    summary: EncounterSummary | null
  ) => Exclude<CardProps['actions'], undefined>['list'];
  isLoading?: boolean;
};

export const SummaryCard: React.FC<SummaryCardProps> = ({
  filters,
  title,
  encounter,
  emptyPlaceholder,
  editable = false,
  editorProps,
  getSummary,
  getCustomActions,
  isLoading: isLoadingProp,
}) => {
  const { mediaMin } = useScreenSize();
  const [isPrinting, setIsPrinting] = useState(false);
  const [activeTab, setActiveTab] = useState<'main' | 'form'>('main');
  const changeDetectedRef = useRef<number | null>(null);

  const { data: { summaries = [] } = {}, isLoading: isLoadingQuery } =
    useGetSummaryList(
      {
        ...filters,
      },
      {
        enabled: !getSummary,
      }
    );

  const { data: { summaries: formSummaries = [] } = {} } = useGetSummaryList(
    {
      ...filters,
      encounterPhase:
        `${filters.encounterPhase}-forms` as typeof filters.encounterPhase,
    },
    {
      // Only fetch form summaries if the encounter phase exists and is not already a form
      enabled:
        !getSummary &&
        !!filters.encounterPhase &&
        !filters.encounterPhase.endsWith('-forms'),
    }
  );

  const isLoading = isLoadingProp || isLoadingQuery;

  const { mutateAsync: updateSummaryMutation, isPending: isUpdatingSummary } =
    useUpdateSummary();

  const summary = useMemo(() => {
    let summary: EncounterSummary | null = null;
    if (getSummary) {
      summary = getSummary(filters);
      if (Array.isArray(summary)) {
        summary = summary[0];
      }
    } else {
      summary = summaries?.[0];
    }
    return summary;
  }, [getSummary, filters, summaries]);

  const formSummary = formSummaries?.[0];

  const summaryString = useMemo(() => {
    if (!summary?.summary_json) {
      return '';
    }

    return summary.summary_json;
  }, [summary]);

  const formSummaryString = useMemo(() => {
    if (!formSummaries?.[0]?.summary_json) {
      return '';
    }
    return formSummary.summary_json;
  }, [formSummary]);

  const { mutateAsync: rateSummary } = useRateSummary();

  const handlePrint = useReactToPrint({
    content: () => {
      const content = document.createElement('div');
      content.style.padding = '32px';
      content.innerHTML = sanitizeSummaryAsHtml(
        [summaryString, formSummaryString].filter(Boolean).join('<br><br>')
      );
      return content;
    },
    documentTitle: encounter?.description || 'Summary',
    onBeforePrint: () => {
      setIsPrinting(true);
    },
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const updateSummary = useCallback(
    async (value: string) => {
      if (!changeDetectedRef.current) {
        return;
      }
      const currentTime = changeDetectedRef.current;
      await updateSummaryMutation({
        summaryId: summary!.summary_id,
        payload: { summary_json: value },
      });
      if (changeDetectedRef.current === currentTime) {
        changeDetectedRef.current = null;
      }
    },
    [summary, updateSummaryMutation]
  );

  const updateSummaryDebounced = useMemo(() => {
    return debounce(updateSummary, 5000);
  }, [updateSummary]);

  const renderSummary = () => {
    let mainSummaryJsx = null;

    if (!summaryString) {
      mainSummaryJsx = (
        <div className="flex justify-center p-4">
          <Empty
            className="max-w-80"
            description={
              emptyPlaceholder ||
              'Summary will be available when patient completes their conversation with lumi'
            }
          />
        </div>
      );
    } else {
      const summaryHtml = parseSummary(summaryString);

      mainSummaryJsx = (
        <>
          {editable ? (
            <Editor
              className="h-full w-full"
              showToolbar
              {...editorProps}
              defaultValue={summaryHtml}
              onChange={(value) => {
                changeDetectedRef.current = Date.now();
                updateSummaryDebounced(value);
              }}
              onBlur={(_, __, editor) => {
                updateSummary(editor.getHTML());
              }}
            />
          ) : (
            <div
              className="pb-10 p-4"
              dangerouslySetInnerHTML={{ __html: summaryHtml }}
            />
          )}
          <div className="absolute bottom-0 right-2">
            <Rating
              label="Rate this note"
              defaultValue={summary?.summary_ratings || 0}
              onCompletion={async ({ value, comment }) => {
                await rateSummary({
                  summary_id: summary!.summary_id,
                  score: value,
                  comment,
                });
                toast.success('Feedback submitted');
              }}
            />
          </div>
        </>
      );
    }

    if (!formSummaryString) {
      return mainSummaryJsx;
    }

    const formSummaryHtml = parseSummary(formSummaryString);

    const formSummaryJsx = (
      <div
        className="pb-10 p-4"
        dangerouslySetInnerHTML={{
          __html: formSummaryHtml,
        }}
      />
    );

    return (
      <div className="flex flex-col">
        <div className="sticky top-0 bg-white z-10 px-4 py-2">
          <Segmented
            options={[
              {
                label: 'Summary',
                value: 'main',
              },
              {
                label: 'Forms Summary',
                value: 'form',
              },
            ]}
            value={activeTab}
            onChange={(value) => {
              setActiveTab(value as 'main' | 'form');
            }}
          />
        </div>
        {activeTab === 'main' ? mainSummaryJsx : formSummaryJsx}
      </div>
    );
  };

  const getCardActions = () => {
    if (isLoading) {
      return [];
    }

    let actions = [];

    if (summaryString || formSummaryString) {
      actions.push(
        ...([
          {
            label: 'Copy',
            icon: <Copy02 />,
            onClick: async () => {
              await copyToClipboard(
                sanitizeSummaryAsText(
                  [summaryString, formSummaryString]
                    .filter(Boolean)
                    .join('<br><br>')
                )
              );
              showToastSuccess('Summary copied to clipboard');
            },
            variant: 'outlined',
          },
          {
            label: 'Copy as rich text',
            icon: <Copy02 />,
            onClick: async () => {
              await copyRichTextToClipboard(
                sanitizeSummaryAsHtml(
                  [summaryString, formSummaryString]
                    .filter(Boolean)
                    .join('<br><br>'),
                  {
                    prefix: [
                      encounter?.description &&
                        `<strong>Encounter Name:</strong> ${encounter.description}<br>`,
                      encounter?.created_at &&
                        `<strong>Date:</strong> ${formatStaticDate(
                          encounter.created_at,
                          'MMM DD, YYYY'
                        )}`,
                    ]
                      .filter(Boolean)
                      .join('<br />'),
                  }
                )
              );
              showToastSuccess('Summary copied to clipboard');
            },
          },
          {
            label: 'Print',
            disabled: isPrinting,
            icon: isPrinting ? <LoadingOutlined /> : <Printer />,
            onClick: async () => {
              handlePrint();
            },
          },
        ] as const)
      );
    }

    actions = [...actions, ...(getCustomActions?.(summary) || [])];
    return actions;
  };

  return (
    <Card
      outlined={false}
      padding={{
        header: 'sm',
        body: 'none',
      }}
      className="flex-1 min-w-[320px] h-full overflow-hidden"
      title={
        <div className="flex justify-between flex-col pr-2">
          <div className="flex items-center gap-2 text-base">
            <Lightbulb02 className="text-primary" />
            {title || 'AI-Generated Summary'}
          </div>
          {editable && (
            <span className="flex items-center gap-1 text-xs text-gray-500 font-normal pl-6">
              <span className="flex items-center gap-1">
                {isUpdatingSummary ? (
                  <>
                    <LoadingOutlined />
                    <span>Saving</span>
                  </>
                ) : (
                  <>
                    <CheckCircleOutlined />
                    <span>Saved</span>
                  </>
                )}
              </span>
              {summary?.updated_at && (
                <>
                  <span>•</span>
                  <span>
                    {moment(summary.updated_at).format('MMM DD, YYYY h:mm A')}
                  </span>
                </>
              )}
            </span>
          )}
        </div>
      }
      actions={{
        overflowMenu: {
          after: mediaMin('sm') ? 1 : 0,
          buttonProps: {
            variant: 'outlined',
          },
        },
        list: getCardActions(),
      }}
    >
      {isLoading ? (
        <div className="p-4">
          <SkeletonLoader active />
        </div>
      ) : (
        renderSummary()
      )}
    </Card>
  );
};
